import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './OrgLanding.css';
import { useAuth } from '../../context/AuthContext';
import { trackEvent } from "../../utils/analytics";

function OrgLanding() {
  const navigate = useNavigate();
  const { currentUser, userRole, loading } = useAuth();

  useEffect(() => {
    if (!loading && currentUser) {
      if (userRole === 'org') navigate('/org/dashboard');
      if (userRole === 'donor') navigate('/give/dashboard');
      if (userRole === 'admin') navigate('/admin/dashboard');
    }
  }, [currentUser, userRole, loading, navigate]);

  if (loading) return null;

  return (
    <div className="org-landing">

      <header className="org-header">
        <div className="logo">somebody.gives</div>
        <div className="org-location-pill">
          📍 Springfield, MO
        </div>
        <div className="header-links">
          <a href="/">For donors</a>
          <button
            onClick={() => navigate('/org/login')}
            className="btn-secondary"
          >
            Sign In
          </button>
        </div>
      </header>

      <section className="org-hero">
        
        <h1>Stop re-soliciting the same supplies.</h1>
        <p className="org-hero-sub">
          List your recurring needs once. Donors adopt them.
          The items ship automatically at the frequency you specify.
        </p>
        <p className="org-hero-mission">
          
        </p>

        <div className="org-example-block">
          
        </div>

        {/* Trust block before CTA */}
        <div className="org-trust-block">
          <div className="org-trust-item">
            <span className="org-trust-icon"></span>
          </div>
          <div className="org-trust-item">
            <span className="org-trust-icon"></span>
            <span>No cost to your organization — ever.</span>
          </div>
          <div className="org-trust-item">
            <span className="org-trust-icon"></span>
            
          </div>
        </div>

        <button
          onClick={() => {
  trackEvent("org_apply_click");
  navigate('/org/signup');
}}
          className="btn-primary btn-large"
        >
          Apply to Join
        </button>
        <div className="org-cta-note">
          Takes 2-3 minutes. We review every application personally.
        </div>
        <div className="org-hero-note">
          Springfield, MO nonprofits only.
        </div>
      </section>

      <section className="org-how">
        <h2>How it works for your organization</h2>
        <div className="org-steps">
          <div className="org-step">
            <div className="step-number">1</div>
            <h3>Apply — simple, quick approval</h3>
            <p>Submit your organization details. We review every
            application personally and respond within 2-3 business
            days. Springfield nonprofits only for now.</p>
          </div>
          <div className="org-step">
            <div className="step-number">2</div>
            <h3>List your recurring needs</h3>
            <p>List the items you re-solicit every month.
Set quantity, frequency, and what one delivery provides.</p>
          </div>
          <div className="org-step">
            <div className="step-number">3</div>
            <h3>Donors take responsibility for your needs</h3>
            <p>Springfield donors browse open needs and take
            responsibility for specific items. You get notified
            every time a need is adopted. Share your page to
            recruit your own donors directly.</p>
          </div>
          <div className="org-step">
            <div className="step-number">4</div>
            <h3>Supplies arrive automatically</h3>
            <p>Orders fire on schedule. Boxes arrive at your door.
            You always know what is coming and when. Your donors
            get notified that their commitment made a difference.</p>
          </div>
        </div>
      </section>

      <section className="org-promise">
        <h2>Our promise to your organization</h2>
        <div className="promise-grid">
          <div className="promise-card">
            <div className="promise-icon">📦</div>
            <h3>Guaranteed delivery</h3>
            <p>If a sponsored item is not delivered within 7 days
            of the scheduled date, we automatically replace it or
            refund the donor. Your supply does not get disrupted.</p>
          </div>
          <div className="promise-card">
            <div className="promise-icon">🔄</div>
            <h3>You always know what's coming</h3>
            <p>Monthly, bi-monthly, or quarterly — on a predictable
            schedule. You always know what is arriving and when,
            so you can plan accordingly.</p>
          </div>
          <div className="promise-card">
            <div className="promise-icon">📣</div>
            <h3>Donors find you</h3>
            <p>Your needs are browsable by Springfield donors.
            We also give you a shareable link so you can recruit
            your own existing supporters directly — no dependency
            on our traffic.</p>
          </div>
          <div className="promise-card">
            <div className="promise-icon">💰</div>
            <h3>No cost to you</h3>
            <p>Participating organizations pay nothing. A small
            platform fee is added to donor transactions. 100% of your requested supply is fulfilled — fees never reduce your items.</p>
          </div>
        </div>
      </section>

      <section className="org-examples">
        <h2>What kinds of needs can you list?</h2>
        <p className="org-examples-sub">
          Any recurring physical supply need that can be fulfilled
          through a standard vendor. Common examples:
        </p>
        <div className="examples-grid">
          <div className="example-pill">🧦 Socks</div>
          <div className="example-pill">👶 Diapers</div>
          <div className="example-pill">🧴 Hygiene kits</div>
          <div className="example-pill">🧺 Laundry detergent</div>
          <div className="example-pill">🧻 Paper products</div>
          <div className="example-pill">🧹 Cleaning supplies</div>
          <div className="example-pill">👙 Undergarments</div>
          <div className="example-pill">🪥 Toothbrushes</div>
          <div className="example-pill">🍼 Baby supplies</div>
          <div className="example-pill">☕ Coffee</div>
        </div>
      </section>

      <section className="org-cta">
        <h2>Ready to make your supply predictable?</h2>
        <p>
          Apply to start receiving reliable monthly supply.
          Each application is reviewed personally, approved quickly.
        </p>
        <button
  onClick={() => {
  trackEvent("org_apply_click");
  navigate('/org/signup');
}}

          className="btn-primary btn-large"
        >
          Apply to Join
        </button>
        <div className="org-cta-note">
          Takes 2-3 minutes to apply.
        </div>
        <div className="signin-link">
          Already approved?{' '}
          <span onClick={() => navigate('/org/login')}>Sign in</span>
        </div>
      </section>

      <footer className="org-footer">
        <div className="footer-content">
          <div className="footer-logo">somebody.gives</div>
          <div className="footer-tagline">
            We turn unpredictable donations into guaranteed supply.
          </div>
          <div className="footer-community">
            Currently serving Springfield, MO — more cities coming soon.
          </div>
          <div className="footer-links">
            <a href="/">For donors</a>
            <a href="/admin">Admin</a>
          </div>
        </div>
      </footer>

    </div>
  );
}

export default OrgLanding;